import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Icon, IconArrowDown, IconArrowUp, IconPlus } from '@flixbus/honeycomb-icons-react';
import { Button, Tag, Tooltip, useSmartPosition } from '@flixbus/honeycomb-react';

import { MODAL_NAMES } from 'common/activeElements/modals/modalNames';
import { getExtraActivityIcon } from 'common/ActivityChip/utils/extraActivities';
import { EXTRA_ACTIVITY_MANAGEMENT_ENABLED } from 'constants/featureFlags';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';
import { useOutsideClick } from 'hooks/useClickOutside';
import { ASSIGNMENT_STATUSES } from 'pages/RideDetails/components/Assigment/const';

import ExtraActivityActionMenu from './components/ExtraActivityActionMenu';
import { EXTRA_ACTIVITIES_TEXT } from './text';
import {
	getAddExtraActivityPayload,
	getCreatableExtraActivityOptions,
	getDeleteExtraActivityPayload,
	getExtraActivityRows,
	getManageExtraActivityPayload,
} from './utils';

import styles from './styles.module.css';

export const ExtraActivities = ({
	placementHorizontal,
	placementVertical,
	laneStacks,
	locations = [],
	rideId,
	sequenceGroupId,
	assignment = {},
	canManageExtraActivities = false,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [activeActionMenuId, setActiveActionMenuId] = useState(null);
	const active = useActiveElements();

	const contentRef = useRef(null);
	const tooltipTargetRef = useRef();
	const tooltipBalloonRef = useRef();
	const menuContentRef = useOutsideClick(() => setIsMenuOpen(false));
	const [contentHeight, setContentHeight] = useState(0);
	const [, smartAlignment] = useSmartPosition({
		active: isMenuOpen,
		targetRef: tooltipTargetRef,
		elementRef: tooltipBalloonRef,
	});
	const stacks = Array.isArray(laneStacks) ? laneStacks : [];
	const hasStacks = stacks.length > 0;
	const creatableExtraActivityOptions = getCreatableExtraActivityOptions({
		assignment,
		locations,
		rideId,
	});
	const canAddExtraActivities =
		EXTRA_ACTIVITY_MANAGEMENT_ENABLED &&
		canManageExtraActivities &&
		creatableExtraActivityOptions.length > 0;

	// Measure helper
	const measure = () => {
		const el = contentRef.current;
		if (!el) return;
		// scrollHeight is the "full" height of content even if collapsed
		setContentHeight(el.scrollHeight);
	};

	// Measure immediately when expanded toggles / data changes
	useLayoutEffect(() => {
		if (!isExpanded) return;
		measure();
	}, [isExpanded, laneStacks]);

	// Keep height in sync with runtime layout changes (fonts, wrapping, responsive width, etc.)
	useEffect(() => {
		if (!isExpanded) return;

		const el = contentRef.current;
		if (!el || typeof ResizeObserver === 'undefined') return;

		const ro = new ResizeObserver(() => {
			measure();
		});
		ro.observe(el);

		// also measure once
		measure();

		return () => ro.disconnect();
	}, [isExpanded]);

	// Hide the whole widget only when there is truly nothing to render:
	// no existing activities AND no way to add new ones AND no assignment
	// to belong to. Breakdown-adjacent segments still have a sequenceGroupId
	// (so existing activities may arrive from the backend), so we keep the
	// widget mounted — the `+` button is disabled via `canManageExtraActivities`.
	if (!hasStacks && !canAddExtraActivities && !sequenceGroupId) {
		return null;
	}

	const handleAddClick = (event) => {
		event.preventDefault();
		event.stopPropagation();
		if (!canAddExtraActivities) return;
		setIsMenuOpen(!isMenuOpen);
	};

	const handleSelectActivity = (activityType) => {
		setIsMenuOpen(false);
		active.setActiveElement(
			MODAL_NAMES.manageExtraActivity,
			getAddExtraActivityPayload({
				activityType,
				availableActivityOptions: creatableExtraActivityOptions,
				locations,
				rideId,
				sequenceGroupId,
			})
		);
	};

	const handleToggleExpanded = () => {
		setActiveActionMenuId(null);
		setIsExpanded((expanded) => !expanded);
	};

	const handleEditActivity = (item) => {
		active.setActiveElement(
			MODAL_NAMES.manageExtraActivity,
			getManageExtraActivityPayload({
				item,
				locations,
			})
		);
	};

	const handleRemoveActivity = (item) => {
		active.setActiveElement(MODAL_NAMES.deleteExtraActivity, getDeleteExtraActivityPayload(item));
	};

	const { itemRowMap, totalRows } = getExtraActivityRows(stacks);
	const headerClassName = classNames(styles.extraActivitiesHeader, {
		[styles.noBorderBottom]: isExpanded,
		[styles.menuOpen]: isMenuOpen,
	});
	const collapseClassName = classNames(styles.extraActivitiesCollapse, {
		[styles.expanded]: isExpanded,
		[styles.actionMenuOpen]: activeActionMenuId,
	});

	const extraActivitiesMenu = (
		<div ref={menuContentRef} className={styles.extraActivitiesMenu} role="menu">
			{creatableExtraActivityOptions.map(({ type, label }) => (
				<Button
					key={type}
					appearance="link"
					role="menuitem"
					extraClasses={styles.extraActivitiesMenuItem}
					onClick={() => handleSelectActivity(type)}
				>
					<span className={styles.extraActivitiesMenuIcon}>{getExtraActivityIcon(type)}</span>
					<span>{label}</span>
				</Button>
			))}
		</div>
	);

	return (
		<div
			className={styles.extraActivitiesContainer}
			style={{
				gridColumn: `${placementHorizontal.from}/${placementHorizontal.to}`,
				gridRow: `${placementVertical.from}/${placementVertical.to}`,
				'--ea-body-h': isExpanded ? `${contentHeight + 3}px` : '0px',
				'--ea-content-h': `${contentHeight}px`,
			}}
		>
			{/* header row */}
			<div className={headerClassName} data-testid="extra-activities-header">
				<div className={styles.extraActivitiesTitle}>{EXTRA_ACTIVITIES_TEXT.title}</div>
				<div className={styles.icons}>
					{EXTRA_ACTIVITY_MANAGEMENT_ENABLED && (
						<Tooltip
							id="extra-activities-add-menu"
							content={extraActivitiesMenu}
							size="content-fit"
							position="top"
							alignment={smartAlignment}
							innerRef={tooltipTargetRef}
							balloonRef={tooltipBalloonRef}
							onClick={handleAddClick}
							extraClasses={styles.onTop}
							active={isMenuOpen}
						>
							<Button
								disabled={!canAddExtraActivities}
								appearance="link"
								aria-expanded={isMenuOpen}
								aria-haspopup="menu"
								aria-label={EXTRA_ACTIVITIES_TEXT.addActivityAriaLabel}
							>
								<Icon InlineIcon={IconPlus} />
							</Button>
						</Tooltip>
					)}

					<Button
						onClick={handleToggleExpanded}
						appearance="link"
						aria-label={
							isExpanded
								? EXTRA_ACTIVITIES_TEXT.collapseActivityAriaLabel
								: EXTRA_ACTIVITIES_TEXT.expandActivityAriaLabel
						}
					>
						<Icon InlineIcon={isExpanded ? IconArrowUp : IconArrowDown} />
					</Button>
				</div>
			</div>

			{/* collapsing row (stays in-flow for subgrid, but container height is clamped to 40px) */}
			<div className={collapseClassName} data-testid="extra-activities-collapse">
				{/* this wrapper is what we measure */}
				<div
					ref={contentRef}
					className={styles.extraActivitiesGrid}
					style={{
						display: 'grid',
						gridTemplateRows: `repeat(${Math.max(totalRows, 1)}, auto)`,
					}}
				>
					{stacks.flatMap((stack) => {
						const { laneKey, placement, items } = stack;
						return items.map((item) => {
							const { id, label, durationMinutes, stationCode, shouldShiftLeft } = item;

							const chipStyle = classNames({
								[styles.chipContent]: true,
								[styles.right]: shouldShiftLeft,
							});

							const rowStart = itemRowMap.get(id);
							const rowEnd = rowStart + 1;

							return (
								<div
									key={`${laneKey}-${id}`}
									className={styles.extraActivityChip}
									style={{
										gridColumn: `${placement.from}/${placement.to}`,
										gridRow: `${rowStart}/${rowEnd}`,
									}}
								>
									<div className={styles.paddingBox}>
										<div className={chipStyle}>
											<span className={styles.extraActivityLabel}>{label}</span>

											<div className={styles.extraActivityMeta}>
												<Tag extraClasses={styles.small} display="subtle">
													{durationMinutes} {EXTRA_ACTIVITIES_TEXT.durationUnit}
												</Tag>
												{stationCode ? (
													<Tag extraClasses={styles.small} display="subtle">
														{stationCode}
													</Tag>
												) : null}
											</div>

											{EXTRA_ACTIVITY_MANAGEMENT_ENABLED &&
												item.status !== ASSIGNMENT_STATUSES.REJECTED && (
													<ExtraActivityActionMenu
														activeMenuId={activeActionMenuId}
														item={item}
														onActiveMenuChange={setActiveActionMenuId}
														onEdit={handleEditActivity}
														onRemove={handleRemoveActivity}
														pushToRight={!shouldShiftLeft}
													/>
												)}
										</div>
									</div>
								</div>
							);
						});
					})}
				</div>
			</div>
		</div>
	);
};
